/**
 * A video.js plugin.
 *
 * In the plugin function, the value of `this` is a video.js `Player`
 * instance. You cannot rely on the player being in a "ready" state here,
 * depending on how the plugin is invoked. This may or may not be important
 * to you; if not, remove the wait for "ready"!
 *
 */

import videojs from 'video.js';
import './polyfills/intersection-observer';

const registerPlugin = videojs.registerPlugin || videojs.plugin;

// Default options for the plugin.
const defaults = {

};

const setSize = (player, options) => {
    if (!player.el() || 
        player.hasClass('vjs-16-9') ||
        player.hasClass('vjs-4-3') ||
        player.hasClass('vjs-fluid')) return;
    player.el().style.width = player.options().width;
    player.el().style.height = player.options().height;
}

const showPlayButton = (player, options) => {
  // Don't show play button if video not started yet
  if (options.timeBeforeStart && options.timeBeforeStart > 0) {
    return;
  }

  if (options.autostart) {
    let videoElement = player.el_.getElementsByTagName('video')[0];

    if (player.vast) {
      player.vast.managePlayEvent('off');
    }

    let showControls = () => {
      // Show button if countdown not in progress
      if (!player.hasClass('vjs-countdown-start-counting')) {
        // Show play button
        player.bigPlayButton.show();
        // Show poster image
        if (player.posterImage) {
          player.posterImage.show();
        }
      }
    };

    let play = (videoElement) => {
      videoElement.play()
        .then(() => {
          // Try restore first advertisment ad
          if (player.vast) {
            player.vast.restorePlayListener();
          }
        })
        .catch(() => {
          showControls();
          // Set callback for this event for case when video has DRM
          player.one('play', showControls);
        });
    }

    if (options.startPlayInViewport) {
      new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          play(videoElement);
          observer.disconnect();
        });
      }, {
        threshold: 1.0
      }).observe(videoElement);
    } else {
      play(videoElement);
    }

    if (player.vast) {
      player.vast.managePlayEvent('on');
    }
  }
};

/**
 * Set one more event listener for seekBar
 * @param {Player} player
 */
const setListenerForSeekBar = (player) => {
  let seekBar = player.controlBar.progressControl && player.controlBar.progressControl.seekBar;
  if (!seekBar) {
    return;
  }
  seekBar.on(player, 'durationchange', seekBar.update);
};

const restartFromBeginning = (player) => {
  let endedPlaylists = [];

  player.on('ended', function() {
    if (endedPlaylists.indexOf(player.currentSrc()) !== -1) return;
    endedPlaylists.push(player.currentSrc());
  });

  player.on('loadstart', function() {
    player.one('canplay', function() {
      var isFirstPlayback = endedPlaylists.indexOf(player.currentSrc()) === -1;
      if (isFirstPlayback) return;
      player.currentTime(0);
    });
  });

  player.on('dispose', function() {
    endedPlaylists = [];
  });
};

/**
 * Function to invoke when the player is ready.
 *
 * This is a great place for your plugin to initialize itself. When this
 * function is called, the player will have its DOM and child components
 * in place.
 *
 * @function onPlayerReady
 * @param    {Player} player
 * @param    {Object} [options={}]
 */

const onPlayerReady = (player, options) => {
    setSize(player, options);
    showPlayButton(player, options);
    setListenerForSeekBar(player);
    if (options.isRestartFromBeginning) {
      restartFromBeginning(player);
    }
};

const wcrPlayer = function(options) {
  // выключаем autoplay на мобильный устройствах и safari
  let is_mobile = /iP(hone|ad|od)|Android|Windows Phone/.test(navigator.userAgent);
  let is_safari = navigator.vendor == 'Apple Computer, Inc.' && /Safari/.test(navigator.userAgent);
  if (is_mobile || is_safari) {
    this.autoplay(false);
  }
  this.ready(() => {
    onPlayerReady(this, videojs.mergeOptions(defaults, options));
  });
};

registerPlugin('wcrPlayer', wcrPlayer);

export default wcrPlayer;
